
/**
 * The open sheet's shared core: `SheetHandleCore`, the one copy of a sheet the
 * client keeps (its rows, cells, model and metadata) behind every caller's
 * `SheetHandle` lease, with the parse that builds it from a wire sheet.
 *
 * `LonaSdk.Client` (`client.ts`) builds a core against the seams it hands in
 * (`SheetHandleDeps`) and uses it through its owner verbs (`parse`,
 * `platformView`, `renamePlan`, `teardown`, …); a promote routine reaches it only
 * through the seam the core builds for it (`installPromoter`). The core never
 * leaves the SDK; an application holds a lease (`sheet-handle.ts`).
 */
import { Rational } from "@tento-core/rational";
import { SheetCoreTree } from "./sheet-core-tree.ts";
import { CorePromotion } from "./sheet-core-promotion.ts";
import type { ListenerErrorHandler } from "./client-seams.ts";
import type { Backend as SheetsBackend, Option, Unsubscribe } from "@tento-lona/sheets";
import { nonnull } from "@tento-core/assert";
import { TypedApi } from "@tento-core/typed-api";
import { OrderedTree } from "@tento-core/ordered-tree";
import { CellId, RowKey, SheetNetworkKey } from "@tento-lona/sheets";
import { Patch } from "@tento-lona/sheets";
import { SheetKey, SheetKeyRegistry } from "./sheet-key.ts";
import { Network } from "../network.ts";
import type { CellTarget } from "../cells/cell-target.ts";
import {
  type Resolvable,
  Resolved,
  Unresolved,
  RowNode,
} from "../rows/row-node.ts";
import { RowLocalId } from "@tento-lona/sheets";
import {
  heldDateCellOf,
  heldTimepointCellOf,
  rowFacadeOf,
  type RowMirror,
  SheetRow,
  SheetRowContext,
} from "../rows/sheet-row.ts";
import { SheetRowFaces, type SheetRowReads } from "../rows/sheet-row-reads.ts";
import { RowFieldWrites } from "../rows/row-field-writes.ts";
// The rows-side sheet's internals are private to `@tento/lona/sheets`. The
// SDK wires the ingestion gate, the promote hook and the instrumentation
// observers onto the rows-side sheets it owns through the package's one SDK
// door, `sheetWiring` — never through its stores.
import { sheetModelOf, sheetReadsOf, sheetWiring } from "@tento-lona/sheets/internal-sdk";
import { CellAccessor, CellSetCoordinator } from "../cells/sheet-cell-accessor.ts";
import {
  registerHandleDoors,
  type SheetHandleInternals,
  type SheetInstrumentation,
} from "../internal/doors.ts";
import type { CellHandle } from "../cells/cell-handle.ts";
import { DateTime, NaiveDate, Utc } from "@tento-chrono";
import { RowAccessor, rowAccessorLocal } from "../rows/rows-accessor.ts";
import { CellStoreView } from "../cells/cell-store-view.ts";
import { SheetHost } from "./sheet-host.ts";
import { Actor } from "../invalidation.ts";
import {
  InvalidationSink,
  RowDataChanged,
  RowsReordered,
  SheetStructureChanged,
} from "../invalidation.ts";
import type { Timescale } from "../view-model/view-model.ts";
import {
  SystemRows,
  SystemRow,
  isBuiltinSheetKey,
  RowNetworkKey,
} from "@tento-lona/sheets";
import { propsFromNodes } from "@tento-lona/sheets/internal-ui";
import type { PageLifecycle } from "../page-lifecycle.ts";
import { wireTypeOf } from "@tento-lona/sheets/platform";
import type { SheetModel } from "@tento-lona/sheets";
import { type CellValue, Json } from "@tento-lona/sheets";
import {
  buildDependencyGraphFromModel,
  DependencyGraph,
  type DependencyGraphReads,
} from "../formula/dependency-graph.ts";
import type { RowHeightPreferences } from "../preferences/row-height-preferences.ts";
import { EventsTable } from "../indices/events-table.ts";
import { DurableHandoffs } from "./durable-handoffs.ts";
import { SheetDataSource } from "./sheet-data-source.ts";
import { RowId } from "@tento-lona/sheets";
import { CellCachingBackend, SeededSyncBackend } from "../sync/sync-backend.ts";
import type {
  SheetModelReads,
  ModelRow,
} from "@tento-lona/sheets";
import type { Sheet } from "@tento-lona/sheets/platform";
import { FormulaDataSource, FormulaEngine, seedCells } from "@tento-lona/sheets/platform";
import { SheetModelCache } from "@tento-lona/sheets/internal-sdk";
import { RowCellCache } from "../sync/row-cell-cache.ts";
import { Formula } from "../formula/formula.ts";
import { type Graph, type GraphSpecNode } from "@tento-lona/sheets";
import { SheetRowTree, treeCellWiring } from "../rows/sheet-tree.ts";
import { SheetId } from "@tento-lona/sheets";
import { ClientLifecycle, DisposeFailures } from "./handle-errors.ts";
import { RowType } from "../rows/row-descriptor.ts";
import { coreOfLease, leaseOver, SheetHandle } from "./sheet-handle.ts";
import { type ParsedSheetInput, parseInputToRawSheet, parseSheetInput, storeParsedRowMeta } from "./sheet-parse.ts";
import { linkNativeArrival, linkStoreBridge, watchRowsSideStructure } from "./rows-side-feeds.ts";

/**
 * The seams a `SheetHandle` is parsed against. SDK-private: only
 * `LonaSdk.Client` builds one (its own, or a copy with preview backends for
 * `parseSheet`), so a handle always shares the client's registries, api and
 * sink.
 */
export type SheetHandleDeps = {
  readonly ids: RowLocalId.Registry;
  /** The client's sheet keys (`client.sheetKeys`), scoped to its current user. */
  readonly sheetKeys: SheetKeyRegistry;
  readonly rows: SheetRow.Registry;
  /** `rows`' frozen reads faces (`SheetRowReads`): what the client's registry reads hand out. */
  readonly rowFaces: SheetRowFaces;
  readonly api: TypedApi<Network.Routes>;
  readonly cellSetCoordinator: CellSetCoordinator;
  readonly host: SheetHost;
  readonly invalidation: InvalidationSink;
  /** The client's liveness, which every row of the sheet also checks. */
  readonly lifecycle: ClientLifecycle;
  /** Client-scoped cell fan-out; every parsed handle registers with it. */
  readonly dataSource: SheetDataSource;
  /** `dataSource` is this handle's own (a `parseSheet` preview's): the
   *  handle disposes it at teardown. The client's is disposed by the client. */
  readonly ownsDataSource: boolean;
  /** Backend for cell fetching, sync, and row operations. */
  readonly backend: CellCachingBackend;
  /** The sheets-side `Backend` beside `backend`: a row's cells by network
   *  key. The client's rows backend, a preview's `rowsBackend`, or one that
   *  rejects (`unreachableSheetsBackend`) where a sheet has no rows side. */
  readonly sheets: SheetsBackend;
  /** Ordinary cache identity follows the client even when a sheet has a seeded fetch transport. */
  readonly sharedRowCache: (id: RowId) => RowCellCache;
  /** A promoted ephemeral sheet retains its seeded backend for reads, but
   * durable cell writes must reach the server through the API. */
  readonly durableCellRoute: "backend" | "api";
  /** The client's sheet-model cache the handle's `model` is hydrated through. */
  readonly modelCache: SheetModelCache;
  /** Height preferences for persisting non-editable row height changes locally. */
  readonly rowHeightPreferences: RowHeightPreferences;
  /**
   * Client-scoped events table, threaded to `SheetRow.Joined.Handle` so
   * `eventOps` can fall back to key-based lookup (`getRowStateByKey`) when a
   * virtual row has no row handle.
   */
  readonly eventsTable: EventsTable;
  /** The client's durable cell and row writes awaiting their verdict
   *  (`CellAccessor.setDurable`, `row.updateDurable`); the client routes
   *  refusals to it. */
  readonly durableWrites: DurableHandoffs;
  /**
   * Drop `handle` from the client's open-handle cache (every alias it is
   * cached under) and forget its rows-side sheet, so the next
   * `client.open` of the same sheet parses a fresh handle — the eviction
   * `handle.dispose()` runs. A handle the client does not cache (a
   * `parseSheet` preview) is left alone.
   */
  readonly evict: (handle: SheetHandleCore) => void;
  /**
   * The client's sheet-list entry for `sheet` (`client.sheets.peek`), or
   * `null` when the list does not hold it: `network.title` follows it.
   */
  readonly listed: (sheet: Network.UserSheet) => Option<Network.UserSheet>;
  /** Propagates a held row's rename and recovers a promote that may have
   *  stored (`RowMirror`): every row of the sheet is built with it. */
  readonly mirrorRow: RowMirror;
  /** The client's row-field ledger and its one transport (`RowFieldWrites`):
   *  every row of the sheet writes its own fields through it. */
  readonly rowWrites: RowFieldWrites;
  /** The client's sink for what a subscriber threw (`ClientConfig.listenerErrorHandler`):
   *  the sheet's cell accessor reports there. */
  readonly listenerErrors: ListenerErrorHandler;
  /** The owning client's page lifecycle (`PageLifecycle`): its cell
   *  accessor records a fetch aborted by the page's unload at info. */
  readonly page: PageLifecycle;
};

/**
 * `formula` with every reference to label `from` renamed to `to`:
 * `rows["from"]` and `cells "from"` — the two spellings the rows-side rename
 * rewrites (`FormulaEngine.propagateLabelRename`), so both models agree.
 */
function rewriteLabelReferences(formula: string, from: string, to: string): string {
  return formula
    .replaceAll(`rows["${from}"]`, `rows["${to}"]`)
    .replaceAll(`cells "${from}"`, `cells "${to}"`);
}

/**
 * The one open sheet the client keeps per sheet: its rows, cells, model and
 * metadata, shared by every caller's `SheetHandle` lease on it. Module-
 * private: an application holds a lease, never the core. The core is torn
 * down when its last holding lease is released (`release`), or by the client
 * (`client.dispose`, a deleted sheet's eviction).
 */
export class SheetHandleCore {
  static {
    // `SheetHandle`'s white-box doors (`internal/doors`), registered once the
    // lease class (loaded first: this module imports it) exists. They read a
    // lease's core directly: no module outside this class reaches its `#`s.
    registerHandleDoors({
      internalsOf: (handle) => coreOfLease(handle).#friendView(),
      instrumentationOf: (handle) => SheetHandleCore.#instrumentation(coreOfLease(handle)),
      prototype: SheetHandle.prototype,
    });
  }

  /** The value behind `instrumentationOf(handle)` (`@tento-lona/internal-testing`). */
  static #instrumentation(core: SheetHandleCore): SheetInstrumentation {
    const rowsSheet = core.#rowsSideSheet;
    const cells = core.#cellAccessor;
    const wiring = sheetWiring(rowsSheet);
    return {
      sheet: rowsSheet,
      get rows(): Iterable<ModelRow> {
        return rowsSheet.allRows();
      },
      get rowHandles(): Iterable<RowCellCache> {
        return cells.instrumentationRowHandles();
      },
      rowHandle: (key) => cells.rowHandle(RowNetworkKey.fromWire(key)),
      peekRowHandle: (key) => cells.peekRowHandle(RowNetworkKey.fromWire(key)),
      ingestion: {
        rows: wiring.ingestionSlot,
        cells: cells.ingestionSlot,
        onSourceInstall: (observer) => wiring.observeSourceInstalls(observer),
        onStoreInstall: (store, observer) =>
          wiring.observeStoreInstalls(store, observer),
        onRecomputeSweep: (observer) => wiring.observeRecomputeSweeps(observer),
        onListenerDrain: (observer) => wiring.observeListenerDrains(observer),
      },
    };
  }

  // ── The owner's verbs (`LonaSdk.Client`) ─────────────────────────────
  // The core never leaves the SDK: the client holds it, and a caller holds a
  // lease (`SheetHandle`) over it. These are the client's operations on the
  // cores it opened; a promote routine gets its own seam (`installPromoter`).

  /**
   * Parse `data` into a new core against the client's seams (`open`,
   * `parseSheet`, an ephemeral open). The parse registers the sheet's rows
   * in the client's registries first; when building the core on them fails
   * afterwards (hydration, an ephemeral open, alias expansion), no core
   * exists to release them at its teardown, so `abandoned` gets their ids
   * before the failure is rethrown. The same when the parse itself throws
   * after registering them (`parseSheetInput`'s `abandoned`). A core that
   * was constructed before the failure (it may have hydrated or opened the
   * rows-side sheet, or taken its facades) is torn down as a release would
   * tear it down: the model-cache entry it made and the facades go with it.
   */
  static parse(
    data: SheetHandle.ParseInput,
    deps: SheetHandleDeps,
    config: Option<SheetHandle.ParseConfig>,
    abandoned: (rows: Set<RowLocalId>) => void,
  ): SheetHandleCore {
    const opts: SheetHandle.ParseConfig = config ?? SheetHandle.ParseConfig.DEFAULT;
    const input = parseSheetInput(data, deps, opts, abandoned);
    const constructed: { core: Option<SheetHandleCore> } = { core: null };
    try {
      return SheetHandleCore.#build(input, deps, opts, (built) => (constructed.core = built));
    } catch (err) {
      const halfBuilt = constructed.core;
      if (halfBuilt != null) {
        // The build's failure is the one thrown; a teardown failure on top of it goes to the client's
        // listener-error handler, and a handler that rethrows does not displace the build's failure.
        try {
          halfBuilt.#teardown();
        } catch (teardownError) {
          try {
            deps.listenerErrors(teardownError);
          } catch {
            // The build's failure below is the one the caller gets.
          }
        }
      }
      const rows = new Set<RowLocalId>(OrderedTree.flatten(input.unresolved.inner).positions.keys());
      for (const id of input.systemRowIds) rows.add(id);
      abandoned(rows);
      throw err;
    }
  }

  /**
   * Install the promote routine `build` makes from this core's promotion seam
   * (`SheetHandleCore.Promotable`): what the routine reads of the core, the
   * promote's two steps that reach into it, and `promoted`, which clears the
   * routine once the sheet is on the server. The seam is built here and handed
   * only to `build`.
   */
  installPromoter(build: (sheet: SheetHandleCore.Promotable) => () => Promise<void>): void {
    this.#promotion.install(build);
  }

  /** The core's rows-side sheet, `null` before its parse hydrated one. */
  get rowsSide(): Option<Sheet> {
    return this.#rowsSide;
  }

  /** The formula rewrites a rename of `renamed` from `from` to `to` needs in this core's rows. */
  renamePlan(renamed: RowLocalId, from: string, to: string): readonly { readonly row: SheetRow.Handle; readonly formula: string }[] {
    return this.#renamePlan(renamed, from, to);
  }

  /** A row of this core changed its label from `oldLabel` to `newLabel`. */
  labelChanged(oldLabel: Option<string>, newLabel: Option<string>): void {
    this.#labelChanged(oldLabel, newLabel);
  }

  /** The value behind `platformOf(handle)` (one stable object per core). */
  platformView(): SheetHandle.Platform {
    return this.#platformDoor();
  }

  /** Every row this core's tree places, the chrome included (`SheetCoreTree.rowIds`). */
  rowIds(): Set<RowLocalId> {
    return this.#tree.rowIds();
  }

  /** Rebuild the formula dependency graph (a rename elsewhere changed what a formula names). */
  rebuildGraph(): void {
    this.#rebuildGraph();
  }

  /** Tear the core down whatever its holds (the client's `dispose`, a deleted sheet's eviction). */
  teardown(): void {
    this.#teardown();
  }

  #sheet: Network.UserSheet;
  /** The seams this handle was parsed against. */
  readonly #deps: SheetHandleDeps;
  /**
   * `network`'s frozen copy, rebuilt when the metadata it copies moves (a
   * promote swaps `#sheet`; a rename moves the sheet list's title).
   */
  #networkCopy: Option<{
    readonly sheet: Network.UserSheet;
    readonly title: Option<string>;
    readonly copy: Network.UserSheet;
  }> = null;

  /** The formula dependency graph; written only by this handle (`parse`, `rebuildGraph`). */
  readonly #dependencyGraph = new DependencyGraph<RowLocalId>();

  #rowsAccessorRef: Option<RowAccessor> = null;
  #lastJoinedTree: Option<SheetRowTree> = null;
  /**
   * The system-row factory this handle was opened with, remembered so every
   * later rebuild agrees with the first one.
   *
   * `SheetHandle.parse` already accepted a factory, but nothing survived it:
   * `SheetViewModel.make` calls `rebuildSystemRows` unconditionally, and both
   * that method and `clone.withViewConfig` hardcoded `Factory.DEFAULT`. So a
   * caller asking for `Factory.PREVIEW` (header only, no footer surface) got
   * the full chrome back on the very next line — which is why an embedded,
   * read-only chart sheet showed a "New Row" button and an "unsaved draft"
   * banner it can never act on. `null` keeps the DEFAULT behaviour.
   */
  #openedFactory: Option<SystemRow.Factory> = null;
  /** The full cell accessor; consumers see the `cells` slice of it. */
  /** Whether the sheet is still local, its promote routine and the attempt in flight (`CorePromotion`). */
  readonly #promotion: CorePromotion;
  /** The sheet's row structure: its trees, generation and chrome (`SheetCoreTree`). */
  readonly #tree: SheetCoreTree;
  readonly #cellAccessor: CellAccessor;
  /** The consumer-facing slice of `cellAccessor` (see `SheetHandle.Cells`). */
  readonly #cellsView: SheetHandle.Cells;
  /** Lazily-built facets; each is one stable object per handle. */
  #localView: Option<SheetHandle.Local> = null;
  #platformCache: Option<SheetHandle.Platform> = null;
  #internalsView: Option<SheetHandleInternals> = null;
  #disposed = false;
  /** The leases holding this core (`hold` / `release`). */
  #holders = 0;
  /** Teardown for the local-write mirror bridge wired in `parse`. */
  #bridgeUnsub: Option<() => void> = null;
  /** Core-owned native cell arrival feeds; independent of the client-wide row-cache bridge. */
  #nativeArrivalUnsub: Option<Unsubscribe> = null;
  /** Release of the rows store revision a seeded backend's answers hold (`SheetDataSource.holdSeedRevision`). */
  #seedRelease: Option<() => void> = null;
  /** Rows-side structural subscription (see `parse`) — bumps
   *  `generation` so the cached joined tree rebuilds on rows-side
   *  tree mutations that bypass this handle. */
  #structuralUnsub: Option<() => void> = null;
  /** Releases this handle's hold on its rows' facades (`Sheet.ownFacades`); `null` once torn down. */
  #releaseFacades: Option<() => void> = null;

  /**
   * The rows-side `Sheet`, set once by `parse` (hydrated through the
   * client's rows accessor) and read through `model`.
   */
  #rowsSide: Option<Sheet> = null;

  private constructor(
    sheet: Network.UserSheet,
    unresolved: RowNode.Tree<typeof Unresolved>,
    deps: SheetHandleDeps,
    systemRowIds: Set<RowLocalId>,
    footerRowIds: Set<RowLocalId>,
    mode: CorePromotion.Mode,
  ) {
    this.#sheet = sheet;
    this.#deps = deps;
    this.#tree = new SheetCoreTree({
      rows: deps.rows,
      resolve: (tree) => RowNode.resolveTree(tree, deps.ids, deps.rows),
      rebuild: (flat) => new RowNode.Tree(OrderedTree.unflatten(flat, Rational.compare), deps.rows),
      invalidation: deps.invalidation,
      actor: SheetHandle.ACTOR,
      sheetKey: () => this.#sheet.id,
      rowsSheet: () => this.#rowsSideSheet,
      insertedAtRoot: (id) => {
        if (this.#shouldMarkInsertedRowKnownEmpty(id)) {
          this.#cellAccessor.markInsertedRowKnownEmpty(RowNetworkKey.of(id));
        }
      },
    }, unresolved, systemRowIds, footerRowIds);
    // Settle→invalidation contract: the accessor emits a scoped per-row
    // cell event when a per-row fetch settles with delivered cells
    // (`CellAccessor.scheduleFetchSettleInvalidation`) — thread the
    // client sink so that emission reaches the app dispatch. See
    // .requirements/structural-rebind — rekeyed-row-repaints-on-fetch-settle.
    const cells = new CellAccessor(
      SheetNetworkKey.fromWire(sheet.id),
      deps.api,
      deps.backend,
      deps.sheets,
      () => this.ensurePromoted(),
      deps.invalidation,
      deps.durableWrites,
      deps.cellSetCoordinator,
      deps.durableCellRoute,
      deps.listenerErrors,
      deps.page,
    );
    cells.setRowCacheResolver(deps.sharedRowCache);
    this.#cellAccessor = cells;
    // `fetch` reaches the network and checks the handle is live
    // (`dispose`); `peekInRange` and `onChange` only inspect the cache and
    // stay allowed on a disposed handle (its cache is empty then). The check
    // reads the handle, not the facade, so a `cells` facade held past
    // `dispose` is refused too. Every entry carries its kind and the row it
    // was read for (`SheetRow.Cell`), so it feeds `CellTarget.cell` with the
    // row check intact.
    const ids = deps.ids;
    /** The row a `RowRef` addresses: its wire key and interned id. */
    const address = (row: SheetHandle.RowRef): { readonly key: string; readonly id: RowLocalId } => {
      const rowKey = SheetHandle.RowRef.key(row);
      if (row instanceof RowLocalId) return { key: rowKey, id: row };
      if (row instanceof RowId) return { key: rowKey, id: ids.resolve(RowKey.of(rowKey)) };
      return { key: rowKey, id: row.id };
    };
    function peekInRange(row: SheetHandle.RowRef, range: NaiveDate.Range): readonly SheetRow.DateCell[];
    function peekInRange(row: SheetHandle.RowRef, range: DateTime.Range<Utc>): readonly SheetRow.TimepointCell[];
    function peekInRange(
      row: SheetHandle.RowRef,
      range: NaiveDate.Range | DateTime.Range<Utc>,
    ): readonly SheetRow.Cell[] {
      const at = address(row);
      if (range instanceof NaiveDate.Range) {
        return cells.peekInRange(RowNetworkKey.fromWire(at.key), range).map((entry) => heldDateCellOf(at.id, entry));
      }
      return cells.peekTimepointsInRange(RowNetworkKey.fromWire(at.key), range).map((entry) => heldTimepointCellOf(at.id, entry));
    }
    this.#cellsView = Object.freeze<SheetHandle.Cells>({
      peekInRange,
      ensureLoaded: (range) => {
        this.#assertLive("cells.ensureLoaded");
        return cells.fetch(range);
      },
      onChange: (cb) => cells.onChange(cb),
    });
    this.#promotion = new CorePromotion({
      network: () => this.network,
      id: () => this.id,
      title: () => this.title,
      generation: () => this.generation,
      serialize: () => this.serialize(),
      rowPositions: () => this.#tree.rowPositionsByRawId(),
      ids: deps.ids,
      rekey: (idRemap) => this.#rekeyInPlace(idRemap),
      adopt: (entry) => {
        this.#sheet = entry;
      },
      flushCells: () => sheetWiring(this.#rowsSideSheet).flushCellsToBackend(),
    }, mode);
  }

  /** `model`'s face, built once per rows-side sheet (`sheetModelOf`). */
  #modelFace: Option<{ readonly sheet: Sheet; readonly face: SheetModel }> = null;

  /**
   * The rows-side sheet this handle renders from — the typed
   * `@tento/lona/sheets` model `SheetRow.Joined.Handle.variant` resolves
   * against — as a read-only view plus the few persisting verbs an app calls
   * (see `SheetModel`): a frozen forwarding face
   * (`sheetModelOf`), the same object on every read, so no cast reaches the
   * `Sheet` behind it. Always present: every handle is parsed through the
   * client's rows accessor.
   */
  get model(): SheetModel {
    const sheet = this.#rowsSideSheet;
    if (this.#modelFace?.sheet !== sheet) {
      this.#modelFace = { sheet, face: sheetModelOf(sheet) };
    }
    return this.#modelFace.face;
  }

  /** The full rows-side `Sheet`; the SDK's own writes go through it. */
  get #rowsSideSheet(): Sheet {
    return nonnull(this.#rowsSide, "SheetHandle.model: read before parse hydrated it");
  }

  /** Incremented on every mutation that invalidates the resolved tree. */
  get generation(): number {
    return this.#tree.generation;
  }

  /**
   * The formula dependency graph, read-only: its frozen read face
   * (`DependencyGraph.readsOf`) — the handle owns the edges.
   */
  get graph(): DependencyGraphReads<RowLocalId> {
    return DependencyGraph.readsOf(this.#dependencyGraph);
  }

  /**
   * The chrome's (system rows: header, footer surface, …) row ids: a live
   * view that refuses every write (`readonlySetView`).
   */
  get systemRowIds(): ReadonlySet<RowLocalId> {
    return this.#tree.systemRowIds;
  }

  /** The footer surface's row ids — a subset of `systemRowIds` — as a live view that refuses every write. */
  get footerRowIds(): ReadonlySet<RowLocalId> {
    return this.#tree.footerRowIds;
  }

  /**
   * This sheet's cells: reads, writes, fetches and change subscriptions (see
   * `SheetHandle.Cells`). The accessor's wiring — row-handle minting,
   * ingestion gates, re-keying, registry binding — is not part of it: the
   * renderer host reaches the gates through `platformOf`, a test harness the
   * row handles through `instrumentationOf` (`@tento-lona/internal-testing`).
   */
  get cells(): SheetHandle.Cells {
    return this.#cellsView;
  }

  /**
   * The local-only tree mutations (see `SheetHandle.Local`): they change this
   * handle's copy of the sheet and announce it, and never reach the server.
   * Off the everyday handle — a renderer host or the SDK reaches them as
   * `platformOf(handle).local`; an app persists through
   * `rows.add` / `rows.delete` / `rows.move`.
   */
  #localDoor(): SheetHandle.Local {
    if (this.#localView != null) return this.#localView;
    const localIdOf = (row: SheetHandle.RowRef): RowLocalId =>
      row instanceof RowLocalId ? row : row instanceof RowId
        ? this.#deps.ids.resolve(RowKey.of(SheetHandle.RowRef.key(row)))
        : row.id;
    const view: SheetHandle.Local = {
      add: (spec, actor) => this.#insertRowLocal(spec, actor),
      addRoot: (id, position, actor) => this.#tree.insertAtRoot(id, position, actor),
      delete: (id, actor) => {
        this.#assertLive("local.delete");
        this.#tree.removeFromTree(id, actor);
      },
      update: (id, patch, actor) => {
        const row = nonnull(
          this.#deps.rows.get(id),
          `SheetHandle.local.update: row ${id.toString()} is not registered`,
        );
        this.#patchRegisteredRow(id, row, patch, actor);
      },
      move: (id, parent, position, actor) => {
        this.#tree.reparent(id, parent, position);
        this.#deps.invalidation.emit(new RowsReordered(actor));
      },
      invalidateStructure: () => {
        this.#tree.invalidateLookupMaps();
        this.#rebuildGraph();
      },
      writeCell: (row, target, data, actor) => this.#paintCellLocal(row, target, data, actor),
      clearCell: (row, target, actor) => this.#clearCellLocal(row, target, actor),
      seedCells: (row, seeded, actor) => seedCells(this.#rowsSideSheet, localIdOf(row), seeded, actor),
      adoptCreated: (created, actor) => rowAccessorLocal(this.rows).adoptCreated(created, actor),
      pushToList: (row) => rowAccessorLocal(this.rows).pushToList(row),
    };
    this.#localView = Object.freeze(view);
    return this.#localView;
  }

  /** The value behind `platformOf(handle)` (`client.ts`); reached through `platformView`. */
  #platformDoor(): SheetHandle.Platform {
    if (this.#platformCache != null) return this.#platformCache;
    const cells = this.#cellAccessor;
    const rowsSheetOf = (): Sheet => this.#rowsSideSheet;
    const localOf = (): SheetHandle.Local => this.#localDoor();
    const assertLive = (verb: string): void => this.#assertLive(verb);
    const view: SheetHandle.Platform = {
      get sheet(): SheetModelReads {
        return sheetReadsOf(rowsSheetOf());
      },
      startFetch: (range) => {
        assertLive("startFetch");
        cells.startFetch(range);
      },
      ensureFresh: (row, range) => {
        assertLive("ensureFresh");
        return cells.ensureFresh(SheetHandle.RowRef.key(row), range);
      },
      peekAll: (row) => {
        const key = SheetHandle.RowRef.key(row);
        const id = row instanceof RowLocalId ? row : row instanceof RowId
          ? this.#deps.ids.resolve(RowKey.of(key))
          : row.id;
        return cells.peekAll(key).map((entry) => heldDateCellOf(id, entry));
      },
      isStale: (row) => cells.isStale(SheetHandle.RowRef.key(row)),
      invalidateRow: (row) => cells.invalidateRow(SheetHandle.RowRef.key(row)),
      forgetFetchCoverage: () => cells.resetFetchCache(),
      get local(): SheetHandle.Local {
        return localOf();
      },
      cellsIn: (row, range) => cells.rowHandle(SheetHandle.RowRef.key(row)).cellsIn(range),
      installIngestionGate: (gate) => {
        cells.setIngestionGate(gate);
        sheetWiring(rowsSheetOf()).installIngestionGate(gate);
      },
      clearIngestionGate: (owner) => {
        cells.clearIngestionGate(owner);
        sheetWiring(rowsSheetOf()).clearIngestionGate(owner);
      },
      setInstallPacer: (pacer) => {
        cells.setInstallPacer(pacer);
        sheetWiring(rowsSheetOf()).setInstallPacer(pacer);
      },
      clearInstallPacer: (owner) => {
        cells.clearInstallPacer(owner);
        sheetWiring(rowsSheetOf()).clearInstallPacer(owner);
      },
      setActive: (active) => cells.setActive(active),
      ensureRowsLoaded: (range) => rowsSheetOf().ensureLoaded(range),
      ensureRootsLoaded: async (range, roots) => {
        const sheet = rowsSheetOf();
        // A demanded row loads its tree (`ModelRow.ensureLoaded`); the loads
        // of one bind gather into batches (`SheetPrefetch.gatherRowFetch`).
        await Promise.all(roots.map((id) => sheet.peekRow(id)?.ensureLoaded(range, null)));
      },
      renderCursors: () => rowsSheetOf().renderCursors(),
      sceneStamp: (rowId) => rowsSheetOf().sceneStamp(rowId),
      ledgerTau: () => rowsSheetOf().ledgerTau(),
      recomputeFormulas: (range) => FormulaEngine.recomputeFormulas(rowsSheetOf(), range),
      setTimescale: (timescale) => rowsSheetOf().setTimescale(timescale),
    };
    this.#platformCache = Object.freeze(view);
    return this.#platformCache;
  }

  /** The value behind `sheetHandleInternals` (`internal/doors`); reached from the static block. */
  #friendView(): SheetHandleInternals {
    if (this.#internalsView != null) return this.#internalsView;
    const rowsSheetOf = (): Sheet => this.#rowsSideSheet;
    const view: SheetHandleInternals = {
      cells: this.#cellAccessor,
      get sheet(): Sheet {
        return rowsSheetOf();
      },
      replaceSystemRows: (defs) => this.#tree.replaceSystemRows(defs),
      rebuildSystemRows: (config) => this.#rebuildSystemRows(config),
      mirrorSystemRowsToRowsSheet: (rows) =>
        this.#tree.mirrorSystemRows(rows),
      rebuildGraph: () => this.#rebuildGraph(),
      unresolvedTree: () => this.tree(Unresolved),
      retain: () => this.hold("open"),
      teardown: () => this.#teardown(),
    };
    this.#internalsView = view;
    return view;
  }

  /** The core over a parsed input (`parse`). */
  static #build(
    input: ParsedSheetInput,
    deps: SheetHandleDeps,
    opts: SheetHandle.ParseConfig,
    /** Told the core as soon as it is constructed, so a later failure can tear it down (`parse`). */
    constructed: (core: SheetHandleCore) => void,
  ): SheetHandleCore {
    const { parsed, sheet, normalizedRows, systemRows, systemRowIds, footerRowIds, unresolved } = input;
    const handle = new SheetHandleCore(
      sheet,
      unresolved,
      deps,
      systemRowIds,
      footerRowIds,
      opts.mode,
    );
    constructed(handle);
    if (opts.factory) handle.#openedFactory = opts.factory;
    // Arm the rebuild memo with the chrome we just installed, so the first
    // `SheetViewModel.make` — which rebuilds unconditionally — is free when it
    // recomputes the same rows (see `rebuildSystemRows`).
    handle.#tree.armSignature(JSON.stringify(systemRows));

    // The sheet's rows are the rows-side sheet's interned facades
    // (`Sheet.facadeFor`, one per row for the sheet's lifetime, answered
    // through this handle while it holds the sheet): the list is filled
    // once the rows-side sheet is known (below); a row added later is minted
    // the same way.
    const rowList: SheetRow.Handle[] = [];
    const makeRow = (sheetRow: SheetRow): SheetRow.Handle => rowFacadeOf(handle.#rowsSideSheet, sheetRow.id);

    // The SDK's own modules hold the sheet through a view lease: it holds
    // nothing (the callers' leases do), and it answers for the sheet as
    // long as the core is live.
    const view = leaseOver(handle, false);
    const accessor = new RowAccessor(
      SheetNetworkKey.fromWire(handle.network.id),
      rowList,
      deps.api,
      deps.invalidation,
      view,
      {
        insertRoot: (id, position, actor) =>
          handle.#tree.insertAtRoot(id, position, actor),
        removeCells: (rowId) => handle.#cellAccessor.removeRow(rowId),
        assertLive: (verb) => handle.#assertLive(verb),
        local: () => handle.#localDoor(),
        placement: () => OrderedTree.flatten(handle.tree(Unresolved).inner),
        rowsSheet: () => handle.#rowsSideSheet,
      },
      makeRow,
      { ids: deps.ids, rows: deps.rows },
      deps.host,
    );

    handle.#rowsAccessorRef = accessor;

    // Store row metadata in backend so index factories can wire
    storeParsedRowMeta(deps.backend, normalizedRows);

    // Build dependency graph: register all rows, wire formula deps
    // Populated from the rows model below, after its hydration.

    // Pass graph + ids to CellAccessor for formula evaluation ordering, and
    // the rows-side-first resolvers its fetch/coverage decisions consult.
    handle.#cellAccessor.bindRegistries(handle.#dependencyGraph, deps.ids, deps.rows, {
      childrenOf: (rowId) => handle.#joinedChildrenOf(rowId),
      rowMeta: (rowId) => handle.#rowMetaForAccessKey(rowId),
      localIds: (rowKey) => handle.#localIdsForAccessKey(rowKey),
    });

    const sheetIdStr = SheetNetworkKey.fromWire(handle.#sheet.id);
    const existing = deps.modelCache.peek(sheetIdStr);
    if (existing) {
      handle.#rowsSide = existing;
    } else {
      const raw = parseInputToRawSheet({ ...parsed, rows: normalizedRows });
      handle.#rowsSide = opts.mode !== "persisted"
        ? deps.modelCache.openEphemeral(raw)
        : deps.modelCache.hydrate(raw);
    }
    sheetWiring(handle.#rowsSideSheet).replaceSystemRows(systemRows);
    buildDependencyGraphFromModel(handle.#dependencyGraph, handle.#rowsSideSheet);
    // Every row of the sheet has ONE facade, interned by the rows-side sheet
    // (`Sheet.facadeFor`): minted and answered through this handle while it
    // holds the sheet (the newest handle over a shared sheet answers) — its
    // lifecycle, its cell writer (promote, persist a seeded row, write,
    // mirror, formulas), its rows' parents.
    handle.#releaseFacades = handle.#rowsSideSheet.ownFacades(
      new SheetRowContext({
        api: deps.api,
        host: deps.host,
        invalidation: deps.invalidation,
        mirror: deps.mirrorRow,
        writes: deps.rowWrites,
        listenerErrors: deps.listenerErrors,
        rowsSheet: handle.#rowsSideSheet,
        lifecycle: {
          assertLive: (verb) => handle.#assertLive(verb),
          cellOwner: (verb) => handle.#cellOwner(verb),
          client: deps.lifecycle,
        },
        cells: handle.#cellAccessor,
        eventsTable: deps.eventsTable,
        rowHeightPrefs: deps.rowHeightPreferences,
        editable: () => handle.editable,
        positionOf: (id) => handle.tree().get(id)?.position ?? null,
        backing: (id) => deps.rows.get(id),
      }),
    );
    for (const r of normalizedRows) {
      rowList.push(rowFacadeOf(handle.#rowsSideSheet, deps.ids.resolve(RowKey.of(r.id.toString()))));
    }
    // Register with the client's data source so shared cell changes flow to
    // the invalidation sink — once the row list is filled: it reads the rows'
    // parents from it. Every SheetId is server-form (ephemerals intern to
    // deterministic UUIDs), so every parsed handle registers.
    deps.dataSource.registerSheet(handle.key, view, handle.#cellAccessor);
    // Copy-on-write: wire the rows-side edit path to the handle's promote so
    // a non-empty cell edit on an ephemeral sheet claims it (see
    // `SourceRow.write`). No-ops once synced.
    sheetWiring(handle.#rowsSideSheet).setPromoteHook(() => handle.ensurePromoted());
    // One title: the rows-side sheet's reads the client's sheet list entry,
    // as `network.title` does, so a rename the list shows is shown there too.
    sheetWiring(handle.#rowsSideSheet).setTitleSource(() => {
      const listed = handle.#deps.listed(handle.#sheet);
      return listed == null ? null : { title: listed.title ?? null };
    });

    // Structural coherence: the rows-side sheet mutates its OWN tree
    // (alias re-expansion on preference change, sync-log reconcile, …)
    // without going through this handle, but `SheetViewModel.joinedTree`
    // caches the joined tree keyed on `handle.generation`. Without a
    // bump the DOM rebinds against the STALE joined rows — e.g. the
    // weather relocation kept painting the old location until a scroll
    // because the re-keyed child's new RowCellCache was never re-bound
    // (ux-test 2026-07-02_dataref-weather-phase-f_c477bb; learning
    // 2026-07-02-weather-relocation-stale-until-scroll). Bump the
    // generation on rows-side structural events so the next `tree`
    // read rebuilds. Bumping is idempotent local state (no emission,
    // no loop); `NewRowInserted` from OTHER sheets sharing the app
    // sink is filtered by membership.
    handle.#structuralUnsub = watchRowsSideStructure(handle.#rowsSideSheet, {
      mirroring: () => handle.#tree.mirroring,
      generation: () => handle.generation,
      bump: () => handle.#tree.invalidate(),
    });


    // ONE cell store: the sheet's rows keep their cells in the rows-side
    // sheet's store (a canonical key: the client's canonical store), and each
    // row's cache is a view over it (`CellStoreView`) — reads and local
    // writes go to the store. The store's change feed fires the caches'
    // listeners (`CellStoreView.link`), carrying the writer's actor so a
    // renderer recognises its own echo; untagged writes (a formula compute, a
    // sync replay) carry the link's own actor, a singleton no plugin holds.
    // A burst inside one store `batch` is one fire per cache. Canonical rows'
    // feed is the client's (`SheetDataSource.attachCanonicalStore`).
    const rowsSide = handle.#rowsSideSheet;
    const localCells = sheetWiring(rowsSide).localCells;
    const canonicalCells = deps.modelCache.canonicalCells;
    handle.#cellAccessor.setStoreViews((accessKey, localId, rh) =>
      CellStoreView.ofSheetRow(rowsSide, localCells, canonicalCells, accessKey, localId, () => rh.cardinality)
    );
    handle.#cellAccessor.setResidentFormulaRecompute((changedRowId, range, dependents) => {
      const handled = new Set<RowLocalId>();
      if (handle.#disposed) return handled;
      for (const id of dependents) {
        if (rowsSide.peekRow(id) instanceof FormulaDataSource) handled.add(id);
      }
      // Accepted mirrors already changed resident facts. Recompute with the
      // model's one engine, without waiting for outstanding dependency demand.
      if (handled.size > 0) FormulaEngine.recomputeDependents(rowsSide, changedRowId, range);
      return handled;
    });
    const bridgeStore = sheetWiring(handle.#rowsSideSheet).localCellFeed();
    if (bridgeStore) {
      handle.#bridgeUnsub = linkStoreBridge(bridgeStore, () => accessor.peekAll(), handle.#cellAccessor, deps.listenerErrors);
    }

    handle.#nativeArrivalUnsub = linkNativeArrival(
      bridgeStore,
      canonicalCells,
      rowsSide,
      handle.#cellAccessor,
      () => handle.#disposed,
      deps.listenerErrors,
    );

    const dataSource = deps.dataSource;
    handle.#cellAccessor.setBulkFetchObserver((row) => {
      dataSource.ingestServerFetchedCells(view, row);
    }, (range) => dataSource.captureServerFetchedCells(view, range));
    // A seeded backend (an ephemeral sheet's) answers from the seed it took
    // at the open: its answers never overwrite a rows-side write since then.
    if (deps.backend instanceof SeededSyncBackend) handle.#seedRelease = dataSource.holdSeedRevision(view);

    // Apply locally-stored row height preferences after all rows are in the registry
    deps.rowHeightPreferences.applyToRegistry(deps.rows);

    return handle;
  }

  /**
   * The sheet's metadata as this handle was opened with it
   * (`Network.UserSheet`: id, title, description, lookup key, creator,
   * role, position, time scale, width; replaced by the server's answer on a
   * promote) — the handle's one read face for its sheet entry. `title`,
   * `description`, `key`, `editable` and `preferredColumnWidthPx` are
   * conveniences over it. A frozen copy, the same object until the metadata
   * moves: its `title` follows the client's sheet list (`client.sheets`)
   * while the list holds this sheet, so a rename through
   * `client.sheets.update` shows here. Sheet metadata changes through
   * `client.sheets.update`, never through this copy.
   */
  get network(): Network.UserSheet {
    const sheet = this.#sheet;
    const listed = this.#deps.listed(sheet);
    const title = listed == null ? sheet.title : listed.title;
    const cached = this.#networkCopy;
    if (cached != null && cached.sheet === sheet && cached.title === title) return cached.copy;
    const copy: Network.UserSheet = Object.freeze({ ...sheet, title });
    this.#networkCopy = { sheet, title, copy };
    return copy;
  }

  /**
   * Storage policy for this handle: `"local"` for ephemeral
   * (in-memory SeededSyncBackend), `"synced"` for server-backed.
   *
   * The intrinsic signal the app layer should read. Capability
   * helpers (`sheetCapabilities`) derive permissions from this +
   * `network.role`; no separate WeakMap / source-tag layer needed.
   */
  /** `true` when this ephemeral sheet was opened from a template. */
  get isTemplate(): boolean {
    return this.#promotion.isTemplate;
  }

  /** The system-row factory this handle was opened with; `null` = DEFAULT. */
  get systemRowFactory(): Option<SystemRow.Factory> {
    return this.#openedFactory;
  }

  get persistence(): "local" | "synced" {
    return this.#promotion.persistence;
  }


  /** The sheet's title; `null` when it has none (`network.title`). */
  get title(): Option<string> {
    return this.network.title ?? null;
  }

  /** The sheet's description; `null` when it has none (`network.description`). */
  get description(): Option<string> {
    return this.#sheet.description ?? null;
  }

  /**
   * The sheet's id: its interned `SheetId`, so two handles of one sheet
   * compare `===`. The wire spelling the list and create routes answer is
   * `network.id`; the text a route takes is `SheetNetworkKey.of(id)`.
   */
  get id(): SheetId {
    return SheetId.of(this.#sheet.id);
  }

  get preferredColumnWidthPx(): Option<number> {
    return this.#sheet.width ?? null;
  }

  get editable(): boolean {
    return this.#sheet.role !== "viewer";
  }

  /** This sheet's key: its id, lookup key and owner (see `SheetKey`). */
  get key(): SheetKey {
    return this.#deps.sheetKeys.fromRaw(this.#sheet);
  }

  /**
   * The client invalidation sink this sheet publishes into. Renderers key
   * per-sink state on it (e.g. `CalendarReadRevision.for(sheet.invalidation)`);
   * emitting on it is the one way to announce a change to this sheet.
   */
  get invalidation(): InvalidationSink {
    return this.#deps.invalidation;
  }

  /**
   * The row metadata this sheet renders `id` from, as its frozen reads face
   * (`SheetRowReads`) — the `client.rowRegistry` entry, or `null` when `id`
   * is not registered. Named for what it returns: `row(query)` is the row's
   * read/write client (`SheetRow.Handle`), found by query rather than by id.
   */
  sheetRow(id: RowLocalId): Option<SheetRowReads> {
    const row = this.#deps.rows.get(id);
    return row == null ? null : this.#deps.rowFaces.of(row);
  }

  /**
   * The sheet's row verbs (`add`, `move`, `delete`, …). Every handle has
   * them — one from `open` and one from `parseSheet` alike: `SheetHandle.parse`,
   * the only way a handle is built, wires the accessor before it returns.
   */
  get rows(): RowAccessor {
    return nonnull(
      this.#rowsAccessorRef,
      "SheetHandle.rows: read during SheetHandle.parse, before it was wired",
    );
  }

  /** The value behind `platformOf(handle).local.add` (see `SheetHandle.Local`). */
  #insertRowLocal(spec: SheetHandle.LocalRowSpec, actor: Actor): RowLocalId {
    this.#assertLive("local.add");
    const sheet = this.#rowsSideSheet;
    const id = sheet.registry.resolveFromRawId(spec.row.id);
    if (sheet.peekRow(id) == null) sheet.addRow(spec.row);
    for (const [date, value] of spec.cells) {
      this.#cellAccessor.rowHandle(RowNetworkKey.fromWire(spec.row.id)).applyCellWriteLocal(date, value, actor, null);
    }
    this.#tree.insertAtRoot(id, spec.position, actor);
    return id;
  }

  /**
   * The value behind `platformOf(handle).local.writeCell` (see
   * `SheetHandle.Local`): paint an edit made elsewhere into the row's
   * cached cell, checked against the row's cardinality first.
   */
  #paintCellLocal(
    row: SheetHandle.RowRef,
    target: CellTarget.DateKeyed,
    data: Json | CellValue,
    actor: Actor,
  ): void {
    this.#assertLive("local.writeCell");
    const address = datedAddress(target);
    const rh = this.#cellAccessor.rowHandle(SheetHandle.RowRef.key(row));
    CellAccessor.checkAddress(rh.cardinality, "date", address.id, "write-cell", "platformOf(handle).local.writeCell");
    rh.applyCellWriteLocal(address.date, data, actor, address.id);
  }

  /**
   * The value behind `platformOf(handle).local.clearCell`: check the address
   * against the row's cardinality, then clear the cell through the client's
   * local-edit chokepoint (every surface showing the row drops it).
   */
  #clearCellLocal(row: SheetHandle.RowRef, target: CellTarget.DateKeyed, actor: Actor): void {
    this.#assertLive("local.clearCell");
    const address = datedAddress(target);
    const key = SheetHandle.RowRef.key(row);
    const rh = this.#cellAccessor.rowHandle(key);
    CellAccessor.checkAddress(rh.cardinality, "date", address.id, "write-cell", "platformOf(handle).local.clearCell");
    this.#deps.dataSource.clearCellLocal(key, address.date, address.id, actor);
  }

  /**
   * Fork alias `id` into an editable group — the structural edit a drag of
   * its synthetic child needs (find the alias with
   * `model.aliasToFork(row)`). Re-keys the alias's template-expanded
   * children into real rows, flips the alias to detached, and persists the
   * promoted children and the alias's new state. Resolves the old → new
   * `RowLocalId` of every promoted row (empty when the alias was childless
   * or already detached), so a caller mid-edit re-resolves the rows the fork
   * re-keyed. On a server-backed sheet the promoted children keep their
   * client-minted ids until the next refresh. Paints first; a failure
   * part-way is compensated before the promise rejects with it: the
   * children the backend created are deleted, the promoted rows dropped, and
   * the alias re-attached to its template and re-expanded.
   */
  detachAlias(id: RowLocalId): Promise<ReadonlyMap<RowLocalId, RowLocalId>> {
    try {
      this.#assertLive("detachAlias");
    } catch (err) {
      return Promise.reject(err);
    }
    return this.#rowsSideSheet.detachAlias(id);
  }

  /**
   * Persist an ephemeral sheet to the server — the one promote verb: it
   * creates the sheet and rows, then replaces internal state with the
   * server-assigned ids. Called from the mutation seam before any op that
   * would change this sheet (copy-on-write) and by a "Save Sheet" CTA. No-op
   * when already `"synced"`. REJECTS with `NotPromotableError` on a `"local"`
   * handle with no promoter (`parseSheet` parsed with `mode: "ephemeral"` or
   * `mode: "template"`, not opened through `openEphemeral`): it can never be
   * saved, so resolving would
   * read as a successful save. Concurrency-safe: N concurrent
   * mutations share one in-flight promote; on success `persistence` flips to
   * `"synced"` so later calls no-op; on failure the shared promise is cleared
   * (a later mutation may retry) and the rejection propagates so the
   * triggering mutation fails atomically (its optimistic apply rolls back).
   * See `ephemeral-promotion` in the lona-sdk-2.0 project docs.
   */
  ensurePromoted(): Promise<void> {
    return this.#promotion.ensure();
  }

  /**
   * The promote's in-place re-key of this core's rows (`CorePromotion.Host.rekey`),
   * run once the server has created the sheet.
   */
  #rekeyInPlace(idRemap: ReadonlyMap<string, string>): void {
    // IN-PLACE promote re-key — NOT a rebuild. The server minted fresh row ids,
    // but instead of re-parsing `serverData` into a new tree we re-point the
    // SHARED `RowLocalId` registry: `modelCache.rekeyRows` aliases each server
    // id onto the EXISTING local id (inbound resolve) and remaps that local id's
    // authoritative key to the server id (outbound ops). Because `deps.ids` IS
    // that same registry, this handle's cached tree (`unresolved`/`resolvedTree`),
    // the rows-side `rowsSheet`, and every live cell subscription keep resolving
    // — now to server ids — with NO new instances.
    //
    // So we deliberately do NOT: re-parse into a fresh `unresolved`, null
    // `resolvedTree`, swap `rowsAccessorRef`/`rowsSheet`, or bump `generation`. No generation
    // bump ⇒ `SheetViewModel.joinedTree` is not invalidated ⇒ no `cell-content-all`
    // rebind ⇒ no re-render. The promote is INVISIBLE: the focused editor keeps
    // focus (no DOM teardown), rows don't re-enter the loading shimmer (no
    // orphaned subscriptions), and seed cells survive (the stores are preserved,
    // not rebuilt). The user can't tell the template was forked.
    //
    // The whole re-key runs under `mirrorStructure`: adopting the server ids is
    // a write into the rows-side sheet, and any `NewRowInserted` /
    // `SheetStructureChanged` it emits describes an id change, not a shape
    // change — the structural subscription must not turn that into a bump (the
    // `cow-promote-rerender` tripwire below is exactly that assertion). The
    // block is synchronous, so nothing else can slip inside the window.
    this.#tree.mirror(() => {
      this.#deps.modelCache.rekeyRows(SheetNetworkKey.fromWire(this.#sheet.id), idRemap);
      // Re-key the lona-js cell-access registry in lockstep. The rows-side
      // stores key by the stable `RowLocalId` OBJECT (so `rekeyRows` above keeps
      // them addressable), but `CellAccessor.rowHandles` keys by the ACCESS KEY
      // — the mutable wire id for sheet-local rows — so without this the
      // post-promote re-resolve builds a fresh EMPTY handle under the server id
      // and every non-focused cell blanks then refills (the whole-sheet COW
      // flash). Runs SYNCHRONOUSLY here, before any `await` and before the async
      // re-render the url push triggers, so every later `rowHandle(serverId)`
      // HITs the populated handle. Does NOT touch `generation` —
      // `cow-promote-rerender` stays absent.
      this.#cellAccessor.rekeyHandles(idRemap);
      // Migrate any locally-stored row-height overrides from the ephemeral wire
      // keys to the server keys. The height store keys by the WIRE string (both
      // its in-memory map and the persisted localStorage record), so without
      // this an ephemeral row's saved height orphans under the stale key on
      // promote. Done at the client layer, which owns the promote + holds the
      // idRemap — NOT inside `rekeyRows`: the rows layer has no preference
      // dependency and must not gain one.
      this.#deps.rowHeightPreferences.migrateKeys(idRemap);
    });
  }

  /** Subscribe to cell data changes. Delegates to `cells.onChange()`. */
  onChange(cb: SheetHandle.Cells.ChangeListener): Unsubscribe {
    return this.#cellAccessor.onChange(cb);
  }

  /**
   * Derive Graph.Props for a data row from its virtual child tree.
   * Returns null if the row has no children or isn't a data row.
   *
   * Walks the rows-side tree (`rowsSheet`), the canonical hydrated tree.
   */
  graphPropsFor(row: SheetHandle.RowRef): Option<Graph.Props> {
    const rowsSide = this.#rowsSideSheet;
    const startRow = rowsSide.peekRow(
      rowsSide.registry.resolveFromRawId(SheetHandle.RowRef.key(row)),
    );
    if (!startRow) return null;
    const childNodes = collectRowsSideGraphSpecNodes(startRow);
    if (childNodes.length === 0) return null;
    return propsFromNodes(childNodes);
  }

  /**
   * Returns a SheetRowTree with cell access, layout, and frozen/footer
   * computation wired. Rows accessed via `tree.get(id)` return
   * SheetRow.Joined with `.cell(column)` backed by the sheet's RowCellCache.
   */
  joinedTree(config: {
    timescale: Timescale;
    host: SheetHost;
    invalidation: InvalidationSink;
  }): SheetRowTree {
    this.#assertLive("joinedTree");
    const resolved = this.tree();
    const tree = new SheetRowTree(resolved, {
      ...this.#tree.joinedTreeSets(),
      idRegistry: this.#deps.ids,
      timescale: config.timescale,
      host: config.host,
      invalidation: config.invalidation,
      cells: treeCellWiring(this.#cellAccessor),
      sheetLifecycle: {
        assertLive: (verb) => this.#assertLive(verb),
        cellOwner: (verb) => this.#cellOwner(verb),
        client: this.#deps.lifecycle,
      },
      api: this.#deps.api,
      editable: this.editable,
      rowsSheet: this.#rowsSideSheet,
      rowHeightPreferences: this.#deps.rowHeightPreferences,
      eventsTable: this.#deps.eventsTable,
      descriptors: this.#deps.rows.descriptors,
      rowMirror: this.#deps.mirrorRow,
      rowWrites: this.#deps.rowWrites,
    });
    this.#lastJoinedTree = tree;

    for (const [_id, joined] of tree.byId) {
      this.#wireFormulaCompute(joined, tree);
    }

    // Wire virtual data-series rows with _adapter via parent subscription.
    // When parent cell data arrives or changes (bulk fetch, incremental extension,
    // or external edit), the adapter runs over all current parent cells and upserts
    // the results directly into the virtual row's _fetched store via applyExternalEdit.
    // This gives sliding-window behaviour: entries grow left and right as the parent's
    // fetched range expands, and update in-place when existing values change.
    // No computeRange / evaluateFormulas involvement — data flows purely via onChange.
    for (const [_id, joined] of tree.byId) {
      if (!RowKey.of(joined.id.toString()).isVirtual()) continue;
      const adapter = joined.graphHooks?.adapter;
      if (!adapter) continue;
      const parentRow = joined.parent();
      if (!parentRow) continue;

      const parentRh = parentRow.joined.rowHandle;
      if (!parentRh) continue;

      const rh = this.#cellAccessor.rowHandle(RowNetworkKey.of(joined.id));
      rh.sourceHandle = parentRh;

      const syncFromParent = () => {
        const edits: { date: NaiveDate.Partial; data: Json | CellValue }[] = [];
        for (const cell of parentRh.allCells()) {
          if (cell.kind !== "date" || cell.value == null) continue;
          const first = adapter(cell.date, null, cell.value)?.[0];
          if (first == null) continue;
          edits.push({ date: cell.date, data: first[1] });
        }
        if (edits.length > 0) rh.batchExternalEdits(edits);
      };

      parentRh.onChange(syncFromParent);  // future parent changes
      syncFromParent();                   // pre-existing parent data (e.g. ephemeral seed)
    }

    return tree;
  }

  /** Take one hold for a lease (`leaseOver`); throws
   *  `SheetHandle.DisposedError` once the core was torn down. */
  hold(verb: string): void {
    this.#assertLive(verb);
    this.#holders++;
  }

  /** The holds its leases take now. */
  get holders(): number {
    return this.#holders;
  }

  /** Whether the core was torn down (its last lease released, the
   *  client's `dispose`, a deleted sheet's eviction). */
  get disposed(): boolean {
    return this.#disposed;
  }

  /** Release one lease's hold; the last release tears the core down. */
  release(): void {
    if (this.#disposed) return;
    this.#holders--;
    if (this.#holders > 0) return;
    this.#teardown();
  }

  /** Tear the core down whatever its holds (the last `release`, the
   *  client's `dispose`, a deleted sheet's eviction). */
  #teardown(): void {
    if (this.#disposed) return;
    this.#disposed = true;
    // Fail the cell subscriptions still active on this sheet (each
    // releases its row subscription and reports to its `onError`). A
    // throwing `onError` stops neither the other subscriptions nor the
    // teardown; the failures are thrown together after it.
    const failures = new DisposeFailures();
    const watchers = [...this.#disposeWatchers];
    this.#disposeWatchers.clear();
    for (const onDisposed of watchers) failures.run(onDisposed);
    const bridgeUnsub = this.#bridgeUnsub;
    this.#bridgeUnsub = null;
    if (bridgeUnsub != null) failures.run(bridgeUnsub);
    const nativeArrivalUnsub = this.#nativeArrivalUnsub;
    this.#nativeArrivalUnsub = null;
    if (nativeArrivalUnsub != null) failures.run(nativeArrivalUnsub);
    const seedRelease = this.#seedRelease;
    this.#seedRelease = null;
    if (seedRelease != null) failures.run(seedRelease);
    const structuralUnsub = this.#structuralUnsub;
    this.#structuralUnsub = null;
    if (structuralUnsub != null) failures.run(structuralUnsub);
    // The rows' facades answer through another handle over the sheet, or —
    // none left — through this one, whose liveness now refuses.
    const releaseFacades = this.#releaseFacades;
    this.#releaseFacades = null;
    if (releaseFacades != null) failures.run(releaseFacades);
    failures.run(() => this.#deps.dataSource.unregisterAccessor(this.#cellAccessor));
    if (this.#deps.ownsDataSource) failures.run(() => this.#deps.dataSource.dispose());
    failures.run(() => this.#cellAccessor.dispose());
    failures.run(() => this.#deps.evict(this));
    failures.rethrow("SheetHandle.dispose: a disposal callback threw");
  }

  /** Throw `SheetHandle.DisposedError` once `dispose` ran; `verb` names the attempt. */
  #assertLive(verb: string): void {
    if (this.#disposed) throw new SheetHandle.DisposedError(verb);
  }

  /** The cell subscriptions active on this sheet, failed on teardown. */
  readonly #disposeWatchers = new Set<() => void>();

  /** This sheet as the owner of a `CellHandle` built by `verb`. */
  #cellOwner(verb: string): CellHandle.Owner {
    return {
      assertLive: () => this.#assertLive(verb),
      watch: (onDisposed) => {
        const watcher = (): void => onDisposed(new SheetHandle.DisposedError(verb));
        this.#disposeWatchers.add(watcher);
        return () => {
          this.#disposeWatchers.delete(watcher);
        };
      },
    };
  }

  // ── Row mutations ──────────────────────────────────────────────

  /** Apply a partial update to a registered row's raw data and invalidate caches. */
  #patchRegisteredRow(
    id: RowLocalId,
    row: SheetRow,
    patch: SheetHandle.LocalRowPatch,
    actor: Actor,
  ): void {
    if (patch.formula !== undefined) {
      // The row's cells are the sheet's (one cell store), computed by the
      // rows side from the formula its row-field ledger holds: a local formula
      // change is confirmed there too — before this copy changes, so the
      // ledger shows it in every copy — unless the sheet already holds it (a
      // rows-side change mirrored here, a re-read).
      const formula = Patch.wire(patch.formula) ?? null;
      const sheet = this.#rowsSideSheet;
      if (sheet.peekRow(id) != null && sheet.rowMeta(id).formula !== formula) {
        const ledger = sheetWiring(sheet).rowWrites;
        ledger.accept(ledger.begin(id, { set: { formula }, attributes: null }, actor, {
          correction: actor,
          quietAttributes: false,
          capture: null,
        }));
      }
    }
    const raw = row.raw;
    if (patch.label !== undefined) raw.label = Patch.wire(patch.label);
    if (patch.heightPx !== undefined)
      raw.heightPx = Patch.wire(patch.heightPx);
    if (patch.lookupKey !== undefined)
      raw.lookupKey = Patch.wire(patch.lookupKey);
    const formulaChanged = patch.formula !== undefined;
    if (patch.formula !== undefined) {
      raw.formula = Patch.wire(patch.formula);
    }
    if (patch.attributes !== undefined)
      raw.attributes = Patch.wire(patch.attributes);
    if (patch.metadata !== undefined)
      raw.metadata = Patch.wire(patch.metadata);
    if (patch.icon !== undefined) raw.icon = Patch.wire(patch.icon);
    row.resetCache();
    // Cosmetic fields (label / heightPx / icon) are read LIVE through the
    // joined tree (`Joined.Handle` delegates to `SheetRow` → `raw`), so a
    // cosmetic-only patch skips the generation bump: `SheetViewModel.joinedTree`
    // keeps its cache and the DOM — including a focused label editor — is not
    // torn down for a rename. Same invariant the COW promote re-key relies on
    // (see `applyPromotion`). Structural fields (lookupKey / formula /
    // attributes / metadata) feed the lookup maps, dependency graph,
    // descriptors, or encodings — those still rebuild.
    const structural = patch.lookupKey !== undefined || formulaChanged ||
      patch.attributes !== undefined || patch.metadata !== undefined;
    if (structural) {
      this.#tree.invalidate();
    } else if (patch.heightPx !== undefined) {
      // Row heights are derived into the cached tree's layout — re-derive in
      // place (emits `LayoutChanged` iff the layout moved) instead of
      // rebuilding the tree.
      this.#lastJoinedTree?.invalidateLayout();
    }
    if (formulaChanged) {
      // Row's dependency set may have changed — rebuild the graph before
      // walking dependents so new references are included and stale ones
      // are dropped.
      this.#rebuildGraph();
      const tree = this.#lastJoinedTree;
      if (tree) {
        const joined = tree.byId.get(id);
        if (joined) this.#wireFormulaCompute(joined, tree);
      }
      this.#cellAccessor.invalidateRow(RowNetworkKey.of(id));
      for (const dep of DependencyGraph.dependentsOnly(this.#dependencyGraph, id)) {
        this.#cellAccessor.invalidateRow(RowNetworkKey.of(dep));
        this.#deps.invalidation.emit(new RowDataChanged(actor, dep, null));
      }
    }
    this.#deps.invalidation.emit(new RowDataChanged(actor, id, null));
  }

  #wireFormulaCompute(
    joined: SheetRow.Joined.Handle,
    tree: SheetRowTree,
  ): void {
    // Skip virtual rows — their formulas are chart adapters (used by DataStore),
    // not display formulas. Evaluating them via Formula.makeComputeRange produces
    // error strings that get displayed in ColumnSeriesSummaryRendererPlugin cells.
    if (!joined.formula) return;
    if (RowKey.of(joined.id.toString()).isVirtual()) return;
    const rh = this.#cellAccessor.rowHandle(RowNetworkKey.of(joined.id));
    rh.setComputeRange(
      Formula.makeComputeRange(joined.formula, joined, tree, this.#platformDoor()),
    );
    rh.kind = "formula";
  }


  /**
   * A row of this sheet changed label from `oldLabel` to `newLabel` (a held
   * row's write through its wiring, or a joined row's — `RowMirror`'s
   * `relabeled`):
   * the lookup maps are re-derived, every formula naming either label is
   * invalidated, and the dependency graph rebuilt so the new label's
   * references resolve.
   */
  #labelChanged(oldLabel: Option<string>, newLabel: Option<string>): void {
    this.#tree.invalidateLookupMaps();

    // Collect formula rows affected by the rename
    const toInvalidate = new Set<RowLocalId>();

    // Formulas referencing the old label (they break or need re-resolve)
    if (oldLabel) {
      const dependers = this.#dependencyGraph.labelToDependers.get(oldLabel);
      if (dependers) for (const id of dependers) toInvalidate.add(id);
    }

    // Formulas referencing the new label (they might now resolve)
    if (newLabel) {
      const dependers = this.#dependencyGraph.labelToDependers.get(newLabel);
      if (dependers) for (const id of dependers) toInvalidate.add(id);
      const unresolved = this.#dependencyGraph.unresolvedNames.get(newLabel);
      if (unresolved) for (const id of unresolved) toInvalidate.add(id);
    }

    for (const depId of toInvalidate) {
      const rh = this.#cellAccessor.rowHandle(RowNetworkKey.of(depId));
      if (rh?.computeRange) {
        rh.invalidateData();
        this.#deps.invalidation.emit(new RowDataChanged(SheetRow.Handle.ACTOR, depId, null));
      }
    }

    // Rebuild dependency graph so new label → row edges are wired.
    // Without this, subsequent cell value changes on the renamed row
    // don't propagate to formulas that now reference it by new label.
    this.#rebuildGraph();
  }

  /**
   * The formulas of this sheet that name row `renamed` by its label `from`
   * (a `rows["from"]` edge resolved to it), each with its text rewritten to
   * name `to` — what a rename propagates (`RowMirror.Renamed`). Read from
   * the dependency graph as it stands before the new label is applied.
   */
  #renamePlan(renamed: RowLocalId, from: string, to: string): readonly { readonly row: SheetRow.Handle; readonly formula: string }[] {
    const plan: { readonly row: SheetRow.Handle; readonly formula: string }[] = [];
    for (const [dependerId, edges] of this.#dependencyGraph.edges) {
      if (dependerId === renamed) continue;
      const byLabel = this.#dependencyGraph.labelToDependers.get(from)?.has(dependerId) ?? false;
      if (!byLabel || !edges.some((edge) => edge.targetRowId === renamed && edge.referenceLiteral === from)) continue;
      const row = this.rows.peek({ id: dependerId });
      const formula = row?.formula ?? null;
      if (row == null || formula == null) continue;
      const rewritten = rewriteLabelReferences(formula, from, to);
      if (rewritten !== formula) plan.push({ row, formula: rewritten });
    }
    return plan;
  }

  /** Rebuild the formula dependency graph from the current row registry. */
  #rebuildGraph(): void {
    // Remove all existing nodes (clears all edges too)
    for (const id of [...this.#dependencyGraph.nodes]) {
      this.#dependencyGraph.removeNode(id);
    }
    FormulaEngine.buildDependencyGraph(this.#rowsSideSheet);
    buildDependencyGraphFromModel(this.#dependencyGraph, this.#rowsSideSheet);
  }

  // ── Tree mutations ─────────────────────────────────────────────


  /**
   * Resolve a row's coverage-relevant metadata by id from the single authority.
   *
   * Row-Metadata Authority workstream — P3 "one resolver". `deps.rows` and the
   * canonical `rowsSheet` are two row-metadata registries kept in sync by hand,
   * and during the migration each can be the ONLY one a given caller populated.
   * Decision sites that need "is this a plain user row?" route through here so
   * they never read a single half-populated registry. Prefer the canonical
   * `rowsSheet` (the structure the renderer walks via `joinedTree`); fall back
   * to `deps.rows` for a row the rows-side sheet does not hold. Phase 2
   * collapses the two registries and this becomes a straight `rowsSheet`
   * read. See `row-metadata-authority.md`.
   */
  #rowMetaFor(id: RowLocalId): Option<CellAccessor.RowMeta> {
    const rs = this.#rowsSideSheet.peekRow(id);
    if (rs) {
      // The sheets-side row exposes `role`/wire-type, not `dataOwner`/`formula`.
      // Derive `dataOwner` from the wire type the same way the bulk-fetch
      // missing-row sweep does (`sheet-cell-accessor.ts`
      // `_shouldMarkMissingRowFetched` → `RowType.get(type).dataOwner`). The
      // `lookupKey` getter is `string | undefined`; normalize to `Option`.
      const wireType = wireTypeOf(rs);
      return {
        type: wireType,
        dataOwner: RowType.get(wireType).dataOwner,
        lookupKey: rs.lookupKey ?? null,
        isFormula: wireType === "formula",
      };
    }
    const row = this.#deps.rows.get(id);
    if (!row) return null;
    return {
      // Fallback (no rows-side row): canonical wire type, not the coarse
      // `descriptor.id`. Mirrors the `wireTypeOf` branch above.
      type: row.raw.type,
      dataOwner: row.descriptor.dataOwner,
      lookupKey: row.lookupKey,
      isFormula: row.formula != null,
    };
  }

  /**
   * The children the last joined tree shows under `rowId` (an access key);
   * `[]` before `joinedTree` has run. The cell accessor warms them when an
   * aggregate row's bulk fetch delivered nothing.
   */
  #joinedChildrenOf(rowId: string): string[] {
    const tree = this.#lastJoinedTree;
    if (tree == null) return [];
    const localId = this.#deps.ids.get(RowKey.of(rowId));
    if (!localId) return [];
    return tree.childrenIds.get(localId)?.map((id) => id.toString()) ?? [];
  }

  /**
   * Single-authority classification for the cell accessor's fetch/coverage
   * decision sites (P3 "one resolver"), by ACCESS key. For a client-expanded
   * alias child the access key IS its canonical lookupKey (`:~weather:{lid}`)
   * and `deps.ids` holds no alias for it, so resolve through the rows-side
   * sheet by key first, then the interned local id.
   *
   * `null` until `joinedTree` has run — the point these resolvers have always
   * come into play — so a fetch issued before the first render keeps its
   * coverage decisions.
   */
  #rowMetaForAccessKey(rowId: string): Option<CellAccessor.RowMeta> {
    if (this.#lastJoinedTree == null) return null;
    const rs = this.#rowsSideSheet.peekByLookupKey(rowId);
    const localId = rs ? rs.id : this.#deps.ids.get(RowKey.of(rowId));
    return localId ? this.#rowMetaFor(localId) : null;
  }

  /**
   * Every rendered row sharing an access key, for the settle→invalidation
   * contract: rows-side first (`peekAllByLookupKey` — every view of one
   * canonical source repaints), else the interned id. `null` until
   * `joinedTree` has run (see `rowMetaForAccessKey`).
   */
  #localIdsForAccessKey(rowKey: string): Option<RowLocalId[]> {
    if (this.#lastJoinedTree == null) return null;
    const shared = this.#rowsSideSheet.peekAllByLookupKey(rowKey);
    if (shared.length > 0) return shared.map((r) => r.id);
    const localId = this.#deps.ids.get(RowKey.of(rowKey));
    return localId ? [localId] : [];
  }

  #shouldMarkInsertedRowKnownEmpty(id: RowLocalId): boolean {
    // A plain `user` row with no lookupKey owns no provider/alias/formula cells,
    // so a freshly-inserted one is vacuously covered. (`!isFormula` is redundant
    // under the `user` gate — a user row never carries a formula — but kept
    // explicit.) Resolving through `rowMetaFor` is what fixed the preview's
    // stuck-shimmer bug; see its doc.
    const meta = this.#rowMetaFor(id);
    if (!meta) return false;
    return meta.dataOwner === "user" && meta.lookupKey == null && !meta.isFormula;
  }

  /**
   * Rebuild system rows (header, footer, all-day events) from the current
   * row data and invalidate the resolved tree cache. Call this after changing
   * attributes that affect system row generation (e.g., children visibility).
   */
  #rebuildSystemRows(config: Option<SheetHandle.RebuildConfig>): void {
    const data = this.serialize();
    const result = SystemRows.build(
      {
        sheet: data,
        debugRows: config?.debugRows ?? [],
        mode: this.#promotion.mode,
        catalog: isBuiltinSheetKey(this.#sheet.lookupKey) ? "builtin" : "user",
        registries: this.#deps.modelCache.registries,
      },
      // Honour the factory this handle was opened with. `build` falls back to
      // DEFAULT on `undefined`, so a handle that never asked for one is
      // unchanged.
      this.#openedFactory ?? null,
    );
    if (config?.timescale) {
      const userRows = [...this.#deps.rows.iter()]
        .filter(([, row]) => !row.isSystemRow)
        .map(([, row]) => row);
      SystemRows.applySecondHeaderRows(result, userRows, config.timescale);
    }
    if (config?.calendarSystems?.length) {
      SystemRows.applySecondaryCalendars(result, config.calendarSystems);
    }
    const { rows: systemRows } = result;
    // An IDENTICAL rebuild is not a change. `SystemRows.build` is pure, so
    // rebuilding while nothing that feeds it moved (the common case: a view
    // model re-made mid-flight, e.g. while a promote is awaiting its POST)
    // produces byte-identical chrome — yet the swap below would still drop and
    // re-insert every system row, bumping `generation` and re-rendering the
    // sheet for nothing. Compare the FULL rows (not just ids/positions) so a
    // content-only change — a header's month label, a footer's banner copy —
    // still counts as a change and still invalidates.
    const signature = JSON.stringify(systemRows);
    if (this.#tree.hasSignature(signature)) return;
    const footerLocalIds = SheetHandle.collectFooterRowIds(systemRows, this.#deps.ids);
    const defs: SheetHandle.SystemRowPlacement[] = systemRows.map((srow) => {
      const localId = this.#deps.ids.resolve(RowKey.of(srow.id.toString()));
      this.#deps.rows.set(localId, srow, null);
      const def: SheetHandle.SystemRowPlacement = {
        id: localId,
        position: srow.position,
        isFooter: footerLocalIds.has(localId),
        children: srow.children?.length
          ? srow.children.map((id) => this.#deps.ids.resolve(RowKey.of(id.toString())))
          : null,
      };
      return def;
    });
    this.#tree.installSystemRows(defs, systemRows, signature);
    if ((config?.emit ?? "structure-changed") === "structure-changed") {
      this.#deps.invalidation.emit(
        new SheetStructureChanged(SheetHandle.ACTOR, SheetId.parse(this.#sheet.id)),
      );
    }
  }

  tree(): RowNode.Tree<typeof Resolved>;
  tree(state: typeof Unresolved): RowNode.Tree<typeof Unresolved>;
  /** The sheet's row tree (`SheetCoreTree.tree`): resolved, or as parsed (`Unresolved`). */
  tree(state?: Resolvable): RowNode.Tree<Resolvable> {
    return state === Unresolved ? this.#tree.unresolved : this.#tree.resolved();
  }

  serialize(): Network.UserSheetWithRows {
    const resolved = this.tree();
    const rows: Network.SheetRow[] = [];

    type TreeNode = (typeof resolved.nodes)[number];
    const collect = (node: TreeNode) => {
      // Skip system rows — they're not network data
      if (this.systemRowIds.has(node.id)) return;

      const raw = this.#deps.rows.raw(node.id);
      if (raw) {
        const childIds: Network.SheetRow["id"][] = [];
        for (const c of node.children) {
          const cr = this.#deps.rows.raw(c.id);
          if (cr) childIds.push(cr.id);
        }
        const row: Network.SheetRow = {
          ...raw,
          position: node.position,
          children: childIds.length > 0 ? childIds : null,
        };
        rows.push(row);
      }
      for (const child of node.children) collect(child);
    };

    for (const root of resolved.nodes) collect(root);
    const sheet: Network.UserSheetWithRows = { ...this.#sheet, rows };
    return sheet;
  }

}

export namespace SheetHandleCore {
  /** What a promote routine reaches of the core it promotes (`installPromoter`; see `CorePromotion.Promotable`). */
  export type Promotable = CorePromotion.Promotable;
}

/** The column and id (`null`: the column's one cell) a date-keyed cell target names. */
export function datedAddress(target: CellTarget.DateKeyed): { readonly date: NaiveDate.Partial; readonly id: Option<CellId> } {
  switch (target.kind) {
    case "date":
      return { date: target.date, id: null };
    case "add-with-id":
      return { date: target.date, id: target.id };
    case "dated-cell":
      return { date: target.cell.date, id: target.cell.id };
  }
}

/**
 * Walk a rows-side `SheetRow` subtree into the shape consumed by
 * `propsFromNodes`.
 */
function collectRowsSideGraphSpecNodes(
  row: ModelRow,
): GraphSpecNode[] {
  const out: GraphSpecNode[] = [];
  for (const child of row.children) {
    out.push(toGraphSpecNode(child));
  }
  return out;
}

function toGraphSpecNode(
  row: ModelRow,
): GraphSpecNode {
  const grandchildren: GraphSpecNode[] = [];
  for (const child of row.children) {
    grandchildren.push(toGraphSpecNode(child));
  }
  const meta = row.sheetReads.rowMeta(row.id);
  return {
    type: wireTypeOf(row),
    ...(row.label != null ? { label: row.label } : {}),
    ...(meta.formula != null ? { formula: meta.formula } : {}),
    ...(meta.attributes ? { attributes: { ...meta.attributes } } : {}),
    ...(grandchildren.length > 0 ? { children: grandchildren } : {}),
  };
}
